import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAppSelector } from 'app/hooks';
import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { Roles } from 'common/constants/roles';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFetchEntities } from 'common/hooks/usePalmFetchEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { fetchApiKeys } from 'common/services/api/ApiKeys/ApiKeys.thunks';
import {
    ColumnDefinition,
    ColumnType,
    Operations } from 'common/types/TypeDefs';

import { ApiKeyCreateContent } from './ApiKeyCreateDrawerContent';
import { ApiKeyCreateSuccessPopup } from './ApiKeyCreateSuccessPopup';
import { ApiKeysDeleteDialogContent } from './ApiKeysDeleteDialogContent';
import {
    addNewFilter,
    clearFilters,
    removeFilter,
    selectFilters,
    selectSuccessDialog,
} from './apiKeysSlice';

export function ApiKeysTab() {

    const typeName = 'API Key';

    const [processing, setProcessing] = useState<boolean>(false);
    const successDialogOpen = useAppSelector(selectSuccessDialog).open;

    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager ]);
    const [sortModel, setSortModel] = usePalmSortModel();

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters);

    const params = useParams();
    const suiteId = params.suiteId ?? '';

    const fetchEntities = usePalmFetchEntities(
        typeName,
        { id: suiteId },
        fetchApiKeys,
        setProcessing);

    const actionDefinitions =  [
        { action: Operations.Delete, disabled: shouldDisableButtons }
    ];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'name', columnName: 'API Key Name', columnType: ColumnType.First },
        { columnId: 'value', columnName: 'API Key Value', columnType: ColumnType.Tooltip },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action }
    ];

    return <>
        <PalmDataGrid
            actionDefinitions={actionDefinitions}
            columnDefinitions={columnDefinitions}
            contentFunction={ApiKeyCreateContent}
            deleteFunction={ApiKeysDeleteDialogContent}
            title={typeName}
            isCreateButtonDisabled={shouldDisableButtons}
            filter={filterProp}
            processing={processing}
            refreshDataGridClick={() => fetchEntities()}
            sortModel={sortModel}
            clientSortHandler={setSortModel}
            shouldShowExportButton={true}
        />
        {successDialogOpen && <ApiKeyCreateSuccessPopup/>}
    </>;
}
